import {Button, Dropdown, Menu, Pagination, Table, Tag as AntdTag, Tag} from "antd";
import { useHistory } from "react-router";
import { FormattedMessage, useIntl } from "react-intl";

import Icon from "../../../components/Icon/Icon";
import Popup from "../../../components/Popup/Popup";
import Spinner from '../../../components/Spinner/Spinner';

import { pageSize, usePublishedResourcesTab } from "./usePublishedResourcesTab";

import { Props, TableData, TabStatus } from "./PublishedResourcesTab.model";

import styles from "./PublishedResourcesTab.module.scss";
import ErrorMessage from "../../../components/ErrorMessage/ErrorMessage";

const PublishedResourcesTab = ({filters}: Props) => {
	const { deleteResource, getResources, onChangePublishedResourcesPagination, setDeletePopup, state, unpublishResource } = usePublishedResourcesTab({filters});
	const { deletePopup, resources, tabStatus, publishedResourcesPagination } = state;
	const intl = useIntl();
	const history = useHistory();

	const columnsApplications = [
		{
			dataIndex: 'title',
			render: (title: string, record: TableData) => (<div className={styles.flexCell}>
				{getIconByResourceType(record.type)}
				<span>{title}</span>
			</div>),
			title: intl.formatMessage({ id: 'table.column.resourceName' }),
		},
		{
			dataIndex: 'date',
			title: intl.formatMessage({ id: 'table.column.date' }),
		},
		{
			dataIndex: 'author',
			title: intl.formatMessage({ id: 'table.column.author' }),
		},
		{
			dataIndex: 'tags',
			render: (tags: string[]) => (
				tags.map((tag, index) => <Tag
					key={index}
					style={{
						border: 'none',
						textTransform: 'uppercase'
					}}
				>
					{tag}
				</Tag>)
			),
			title: intl.formatMessage({ id: 'table.column.tags' }),
		},
	{
			render: (record: TableData) => {
				return <Dropdown overlay={renderDropdown(record)}>
					<div className={styles.iconWrapper} ><Icon name="ellipsis" color="text" /> </div>
				</Dropdown> 
			},
		}
	];

	const renderDropdown = (item: TableData) => {
		return <Menu>
			<Menu.Item key="1">
				<Button onClick={(event) => { event.stopPropagation(); unpublishResource(item.id); }} type="link"><FormattedMessage id="buttons.unpublish" /></Button>
			</Menu.Item>
			<Menu.Item key="2">
				<Button className={styles.redBtn} onClick={(event) => { event.stopPropagation(); setDeletePopup(item.id);}} type="link">
					<FormattedMessage id="buttons.delete" />
				</Button>
			</Menu.Item>
		</Menu>
	}

	const getIconByResourceType = (resourceType: string) => {
		switch (resourceType) {
			case 'Audio': return <Icon name="microphone" color="gray" type="solid" />
			case 'Video': return <Icon name="video" color="gray" type="solid" />
			case 'Text': return <Icon name="file-alt" color="gray" type="solid" />
		}
	}
	const getMobileIconByResourceType = (resourceType: string) => {
		switch (resourceType) {
			case 'Audio': return <Icon name="microphone" color="gray" type="solid" width="25px" height="25px"/>
			case 'Video': return <Icon name="video" color="gray" type="solid" width="25px" height="25px"/>
			case 'Text': return <Icon name="file-alt" color="gray" type="solid" width="25px" height="25px"/>
		}
	}

	const onApplicationRow = (rowData: TableData) => {
		return {
			onClick: () => {
				history.push(`/resource-list/${rowData.id}`);
			},
		};
	}

	return <div className={styles.tabContent}>
		<Spinner spinning={tabStatus === TabStatus.Loading} />
		<ErrorMessage error={tabStatus === TabStatus.Error} onRefresh={getResources} showReport={false}/>
		<div className={styles.table}>
			<Table
				columns={columnsApplications}
				dataSource={resources}
				pagination={false}
				onRow={onApplicationRow}
			/>
		</div>
		<div className={styles.contentMobile}>
		{resources?.map((resource) => {
			return (
				<div
					className={styles.card}
					onClick={() => history.push(`/resource-list/${resource.id}`)}
				>
					<div className={styles.cardContainer}>
						<div className={styles.iconWrapper}>
							{getMobileIconByResourceType(resource.type)}
						</div>
						<div>
							<h2>
								<FormattedMessage id="table.column.resourceName" />
							</h2>
							<div className={styles.flexCell}>
										<span className={styles.mobileResourceName}>
											{resource.title}
										</span>
							</div>
							<div className={styles.row}>
								<h2 className={styles.label}>
									<FormattedMessage id="table.column.date" />
								</h2>
								<span>{resource.date}</span>
							</div>
							<div>
								{resource.tags.length ?
									<h2>
										<FormattedMessage id="table.column.tags" />
									</h2> : <></>
								}
								{resource.tags?.map((tag, index) => (
									<AntdTag
										key={index}
										style={{
											border: 'none',
											textTransform: 'uppercase',
										}}
									>
										{intl.formatMessage({ id: `researchAreas.${tag}` })}
									</AntdTag>
								))}
							</div>
							<div className={styles.actions}>
								<Button onClick={(event) => { event.stopPropagation(); unpublishResource(resource.id); }} type="primary"><FormattedMessage id="buttons.unpublish" /></Button>
								<Button className={styles.redBtn} onClick={(event) => { event.stopPropagation(); setDeletePopup(resource.id); }} type="link">
									<FormattedMessage id="buttons.delete" />
								</Button>
							</div>
						</div>
					</div>
				</div>
			);
		})}
	</div>
		<div className={styles.paginationWrapper}>
			<Pagination
				className={styles.pagination}
				current={publishedResourcesPagination.page}
				pageSize={pageSize}
				total={publishedResourcesPagination.total}
				onChange={onChangePublishedResourcesPagination}
				showSizeChanger={false}
			/>
		</div>
		{deletePopup && <Popup onClose={() => { setDeletePopup('');}} title={{action: '', target: ''}}>
			<div className={styles.popupContent}>
				<Icon name="trash" color="accent" width="100px"  height="100px" />
				<h1><FormattedMessage id="popupTitles.deleteConfirmation" /></h1>
				<p><FormattedMessage id="resources.deleteConfirmation" /></p>
				<div className={styles.popupActions}>
					<Button size="large" type="primary" onClick={deleteResource.bind(null, deletePopup )}>
						<FormattedMessage id="buttons.delete" />
					</Button>
					<Button size="large" type="default" onClick={() => { setDeletePopup(''); }} >
						<FormattedMessage id="buttons.cancel" />
					</Button>
				</div>
			</div>
		</Popup>}
	</div>
}
export default PublishedResourcesTab;